import { Component, Inject, OnInit, ViewEncapsulation, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project, Pipeline, ProjectTemplate } from 'app/modules/projects/projects.types';
import { ProjectType, PropertyType, ProjectStage, FieldDef } from 'app/core/models/account.types';
@Component({
  selector: 'project-add-child',
  templateUrl: './project-add-child.component.html',
  styleUrls: ['./project-add-child.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProjectAddChildComponent implements OnInit, OnDestroy {
  addProjectForm: UntypedFormGroup;
  addProjectByUrlForm: UntypedFormGroup;
  fieldDefs: FieldDef[] = [];
  project: Project;
  displayColumns: any[];
  options: [];
  projectTypes: ProjectType[];
  propertyTypes: PropertyType[];
  projectStages: ProjectStage[];
  projectTemplates: ProjectTemplate<any>[];
  isDealsDetailView: boolean = true;
  duplicateProjectId: number = 0;
  duplicateProjects: Project[] = [];
  duplicateAddressProjects: Project[] = [];

  private _unsubscribeAll: Subject<void>;
  public uploadedFile: any;
  public projectAlreadyChild: boolean;
  public activeButtonIndex: number;

  /**
   * Constructor
   *
   * @param {MAT_DIALOG_DATA} data
   * @param {matDialogRef<ProjectAddChildComponent>} matDialogRef
   * @param { FormBuilder} _formBuilder
   * @param {Router} _router
   * @param {ChangeDetectorRef} _changeDetectorRef
   * @param {ProjectsService} _projectsService
   */
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<ProjectAddChildComponent>,
    private _formBuilder: UntypedFormBuilder,
    private _router: Router,
    private _changeDetectorRef: ChangeDetectorRef,
    private _projectsService: ProjectsService,
  ) {
    this._unsubscribeAll = new Subject();
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    // Create the form.
    this.addProjectForm = this._formBuilder.group({
      title: [''],
      property_type_id: ['', [Validators.required]],
      project_type_id: [''],
      pipeline_id: [''],
      template_id: ['', [Validators.required]],
      property: this._formBuilder.group({
        property_type_id: [''],
        address: this._formBuilder.group({
          address_string: '',
          street: '',
          city: [''],
          state: '',
          postal_code: '',
          latitude: '',
          longitude: '',
        }),
      }),
    });

    this.addProjectByUrlForm = this._formBuilder.group({
      pipeline_id: [''],
      listing_url: [''],
    });

    // Attempt to automatically select the project template based on the selected property type.
    this.addProjectForm.get('property_type_id').valueChanges.subscribe((value) => {
      let propertyTypeId = value;
      const index = this.propertyTypes.findIndex((pType) => pType.id === propertyTypeId);
      if (index) {
        let propertyType = this.propertyTypes[index];
        if (propertyType) {
          let foundTemplates = this.projectTemplates.filter((template) =>
            template.name.toLowerCase().includes(propertyType.name.toLowerCase()),
          );
          if (foundTemplates && foundTemplates.length > 0) {
            this.addProjectForm.get('template_id').setValue(foundTemplates[0].id);
          }
        }
      }
    });

    this.projectTypes = this.data.data.options.project_types.filter((option) => !option.hidden);
    this.propertyTypes = this.data.data.options.property_types.filter((option) => !option.hidden);
    this.projectStages = this.data.data.options.property_types.filter((option) => !option.hidden);
    this.projectTemplates = this.data.data.options.project_templates
      .filter((option) => !option.hidden && option.related_to_type == 'Project')
      .sort((a, b) => a.rank - b.rank);

    // Set ProjectTemplate<any> to first entry.
    if (this.projectTemplates && this.projectTemplates.length > 0) {
      this.addProjectForm.get('template_id').setValue(this.projectTemplates[0].id);
    }

    if (this.data.project) {
      this.addProjectForm.get('template_id').setValue(this.data.project.template?.id);
    }
    this._projectsService.pipeline$.pipe(takeUntil(this._unsubscribeAll)).subscribe((pipeline: Pipeline) => {
      let definition = pipeline?.table_definition;
      if (definition) {
        this.displayColumns = definition.fields;
        this.displayColumns = this.displayColumns.filter((column) => column !== 'project_stage_id');
        // Since we already add the project_stage_id manually, we should remove if present.
      }
      this.addProjectForm.get('pipeline_id').setValue(pipeline?.id);
      this.addProjectByUrlForm.get('pipeline_id').setValue(pipeline?.id);
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onFileChanged($event) {
    let file = $event.target.files[0];
    this.uploadedFile = file;
  }

  onBlur() {
    this.duplicateProjectId = 0;
    if (this.addProjectForm.get('title').value) {
      this._projectsService
        .getProjectExists(this.addProjectForm.get('title').value, this.addProjectForm.get('pipeline_id').value)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((data) => {
          if (data) {
            if (data.projects) {
              this.duplicateProjects = data.projects;
            }
          }
        });
    }
  }

  addressBlur() {
    this.duplicateProjectId = 0;
    let property = this.addProjectForm.get('property').value;
    if (
      property.address.street !== '' &&
      property.address.city !== '' &&
      property.address.state !== '' &&
      property.address.postal_code !== ''
    ) {
      let queryFilters = {
        address: property.address.street,
        city: property.address.city,
        state: property.address.state,
        zip: property.address.postal_code,
      };

      let queryFiltersLocation = {
        location: {},
      };

      let addressString = `${queryFilters['address']} ${queryFilters['city']}, ${queryFilters['state']} ${queryFilters['zip']}`;

      queryFiltersLocation.location = { addressString: addressString, within: '0.1mi' };

      this._projectsService
        .getProjectsNoPipeline(['pipeline_id'], queryFiltersLocation, 0, 20, 'projects.created_at', 'desc', '', true)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((data) => {
          if (data) {
            if (data.projects) {
              this.duplicateAddressProjects = data.projects;

              this._isDealAlreadyChildren(this.data.project.children, this.duplicateAddressProjects);

              this._changeDetectorRef.markForCheck();
              this._changeDetectorRef.detectChanges();
            }
          }
        });
    }
  }

  /**
   * @param childrenDealsArray 'children' array property returned from MAT_DIALOG_DATA project object.
   * @param addressArray the 'address_string' property value returned from duplicatedAddressProjects array.
   * @returns boolean value which identifies if child deal already exists as child portfolio.
   */
  private _isDealAlreadyChildren(childrenDealsArray: Array<any>, addressArray?: Array<any>): boolean {
    if (addressArray && addressArray.length > 0) {
      return (this.projectAlreadyChild = childrenDealsArray.some(
        (el) => el.address_string === addressArray[0]['address_string'],
      ));
    } else {
      return (this.projectAlreadyChild = false);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  setAddressParts(addressParts) {
    this.addProjectForm
      .get('property')
      .get('address')
      .get('city')
      .setValue(addressParts.locality || addressParts.sublocality_level_1);
    this.addProjectForm.get('property').get('address').get('state').setValue(addressParts.administrative_area_level_1);
    this.addProjectForm.get('property').get('address').get('postal_code').setValue(addressParts.postal_code);
    if (addressParts.street_number && addressParts.route) {
      this.addProjectForm
        .get('property')
        .get('address')
        .get('street')
        .setValue(addressParts.street_number + ' ' + addressParts.route);
    } else if (!addressParts.street_number && addressParts.route) {
      this.addProjectForm.get('property').get('address').get('street').setValue(addressParts.route);
    } else {
      this.addProjectForm.get('property').get('address').get('street').setValue('');
    }
    this.addProjectForm.get('property').get('address').get('latitude').setValue(addressParts.lat);
    this.addProjectForm.get('property').get('address').get('longitude').setValue(addressParts.lng);
    this._changeDetectorRef.detectChanges();

    //since blur doesn't work for google-places, we need to call it manually
    this.addressBlur();
  }

  setAddress(addressString) {
    //this.projectForm.get('deal').get('property').get('address').get('address_string').setValue(addressString);
  }

  goToDeal(): void {
    this._router.navigateByUrl('/projects/' + this.project.pipeline_id + '/' + this.project.id + '/details/overview');
    this.discard();
  }

  goToProject(project): void {
    this._router.navigateByUrl('/projects/' + project.pipeline_id + '/' + project.id + '/details/overview');
    this.discard();
  }

  /**
   * Responsible for triggering a PUT request to add new or existing deals to a children´s property Portfolio.
   */
  public save(): void {
    this.addProjectForm.disable();

    this.addProjectForm
      .get('property')
      .get('property_type_id')
      .setValue(this.addProjectForm.get('property_type_id').value);

    let project = this.addProjectForm.value;

    project.title = this.addProjectForm.get('property').get('address').get('street').value;

    this._projectsService
      .saveProject(project, null, null, false)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((project) => {
        // Add child to parent.
        let parentProject = this.data.project;
        if (parentProject.child_ids) {
          parentProject.child_ids.push(project.id);
        } else {
          parentProject.child_ids = [project.id];
        }
        this._projectsService
          .saveProject(parentProject)
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe((parentProject) => {
            this.matDialogRef.close({ project: parentProject });
          });
      });
  }

  saveByUrl(): void {
    this.addProjectByUrlForm.disable();

    let project = this.addProjectByUrlForm.value;
    //project.pipeline_id = this._projectsService.pipelineId;
    this._projectsService
      .saveProject(project)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((project) => {
        this.matDialogRef.close({ project: project });
      });
  }

  /**
   * Discard the message
   */
  discard(): void {
    this.matDialogRef.close();
  }

  updated() {
    this._projectsService.saveProject(this.project).pipe(takeUntil(this._unsubscribeAll)).subscribe();
  }

  lookupField(name) {
    let fields = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (fields.length > 0) {
      return fields[0];
    } else {
      return null;
    }
  }
}
